import { Menu, Skeleton, Tooltip } from '@mantine/core';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';

import { Portal } from '@mantine/core';
import { Text } from '@mantine/core';
import { Group } from '@mantine/core';
import { IconCalendar, IconDotsVertical, IconEye, IconPencil } from '@tabler/icons-react';
import { IconPlus } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import panic from 'errors/panic';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useOrganizationModules from 'features/organization-modules/use-organization-modules';
import {
  ADD_REVISION_PAGE_PATH,
  DEVICE_REVISION_PLANS_PAGE_PATH,
  EDIT_DEVICE_PAGE_PATH,
  REDIRECT_PAGE_PATH,
} from 'routes/paths';

/**
 * Parameters of the RevisionPlanActions component.
 */
export interface RevisionPlanActionsProps {
  revisionPlanId: number;
  type: string;
  nextRevision?: string;
  organization: { organizationId: number; organizationName: string };
  department: { departmentId: number; departmentName: string };
  deviceId: number;
  deviceType: { deviceTypeId: number; deviceTypeName: string };
  showCalendar?: boolean;
  compactOnTablet?: boolean;
  keepPrimaryActionOnMobile?: boolean;
  parentDevice?: { deviceId: number; deviceName: string };
}

/**
 * Displays actions for a revision plan in a table.
 */
export default function RevisionPlanActions({
  revisionPlanId,
  type,
  nextRevision,
  organization,
  department,
  deviceId,
  deviceType,
  showCalendar = true,
  compactOnTablet = false,
  keepPrimaryActionOnMobile = false,
  parentDevice,
}: RevisionPlanActionsProps) {
  const { getAction, roleId } = useApi();
  const { revisions } = useOrganizationModules();
  const { isMobile, tableActionMode } = useResponsiveDesign();
  const isCompact = compactOnTablet && tableActionMode !== 'desktop';
  const menuOnly = compactOnTablet && tableActionMode === 'mobile' && !keepPrimaryActionOnMobile;
  const [opened, setOpened] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(roleId !== ROLE_ADMIN_ID);
  const [canAssignRevisions, setCanAssignRevisions] = useState<boolean>(roleId === ROLE_ADMIN_ID);
  const [canEditDevices, setCanEditDevices] = useState<boolean>(roleId === ROLE_ADMIN_ID);

  const addRevisionPageLink = useMemo(() => {
    // Build the devices array with the new format
    const initialDevices = [{ deviceId: parentDevice?.deviceId ?? deviceId, termId: revisionPlanId }];
    const devicesParam = encodeURIComponent(JSON.stringify(initialDevices));

    let query = `revisionName=${encodeURIComponent(type)}`;
    query = `${query}&devices=${devicesParam}`;
    query = `${query}&organizationId=${organization.organizationId}&departmentId=${department.departmentId}&deviceTypeId=${deviceType.deviceTypeId}`;

    return `${ADD_REVISION_PAGE_PATH.original}?${query}`;
  }, [revisionPlanId, type, deviceId, organization, department, deviceType, parentDevice]);

  const calendarPageLink = useMemo(() => {
    const deadline = new Date(nextRevision!);
    const query = `year=${deadline.getFullYear()}&month=${
      deadline.getMonth() + 1
    }&day=${deadline.getDate()}&tab=calendar&revisionPlanId=${revisionPlanId}`;
    const targetUrl = `${DEVICE_REVISION_PLANS_PAGE_PATH.original}?${query}`;

    return `${REDIRECT_PAGE_PATH.original}?to=${encodeURIComponent(targetUrl)}`;
  }, [deviceId, revisionPlanId, nextRevision]);

  const editPageLink = useMemo(
    () =>
      `${EDIT_DEVICE_PAGE_PATH.insert({
        deviceId: parentDevice?.deviceId ?? deviceId,
      })}?revisionPlanId=${revisionPlanId}`,
    [parentDevice, deviceId, revisionPlanId]
  );

  // Check permissions on open.
  useEffect(() => {
    const action = getAction('AuthUserHasPermissionInDepartment');

    if (roleId === ROLE_ADMIN_ID) {
      setCanAssignRevisions(true);
      setCanEditDevices(true);
      setLoading(false);
    } else {
      Promise.all([
        action({ parameters: { departmentId: String(department.departmentId), permissionSlug: 'assign-revisions' } }),
        action({ parameters: { departmentId: String(department.departmentId), permissionSlug: 'manage-devices' } }),
      ])
        .then(([{ hasPermission: canAssignRevisions }, { hasPermission: canEditDevices }]) => {
          setCanAssignRevisions(canAssignRevisions);
          setCanEditDevices(canEditDevices);
        })
        .catch(panic)
        .finally(() => setLoading(false));
    }
  }, [roleId, department.departmentId]);

  const primaryAction = useMemo(() => {
    if (menuOnly) {
      return null;
    }

    if (loading) {
      return isCompact ? <Skeleton w={32} h={32} radius={4} /> : <Skeleton w={120} h={40} radius={4} />;
    }

    if (isCompact) {
      const label = canEditDevices ? 'Editovať' : 'Zobraziť';
      const icon = canEditDevices ? <IconPencil /> : <IconEye />;
      return (
        <Tooltip label={label} events={{ hover: true, focus: true, touch: true }}>
          <ActionIcon variant="secondary" size="md" component={Link} to={editPageLink}>
            {icon}
          </ActionIcon>
        </Tooltip>
      );
    }

    return (
      <Button
        w={120}
        size="md"
        component={Link}
        to={editPageLink}
        variant="secondary"
        leftIcon={canEditDevices ? <IconPencil /> : <IconEye />}
      >
        {canEditDevices ? 'Editovať' : 'Zobraziť'}
      </Button>
    );
  }, [canEditDevices, editPageLink, isCompact, loading, menuOnly]);

  return (
    <Group spacing={12} noWrap>
      {primaryAction}
      <Menu position="bottom-end" zIndex={2000} opened={opened} onChange={setOpened}>
        <Menu.Target>
          <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'}>
            <IconDotsVertical stroke="1.5" />
          </ActionIcon>
        </Menu.Target>
        <Portal>
          <Menu.Dropdown>
            <Menu.Label>
              <Text maw={160} truncate>
                {type}
              </Text>
            </Menu.Label>
            {menuOnly && (
              <Menu.Item
                component={Link}
                to={editPageLink}
                icon={canEditDevices ? <IconPencil stroke="1.5" size={20} /> : <IconEye stroke="1.5" size={20} />}
              >
                {canEditDevices ? 'Editovať' : 'Zobraziť'}
              </Menu.Item>
            )}
            {showCalendar && nextRevision && (
              <Menu.Item
                component={Link}
                to={calendarPageLink}
                icon={<IconCalendar stroke="1.5" size={20} />}
                color="gray.8"
              >
                Zobraziť v kalendári
              </Menu.Item>
            )}
            {revisions && canAssignRevisions && (
              <Menu.Item
                component={Link}
                to={addRevisionPageLink}
                icon={<IconPlus stroke="1.5" size={20} />}
                color="gray.8"
              >
                Zadať revíziu
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Portal>
      </Menu>
    </Group>
  );
}
